<template>
  <div>
    <Modal name="fix-item"
      :title="$t('pos_mapping_anomalies.fix_anomaly')"
      :width="600" :actions="[
        {name: $t('back'), action: 'close'},
        {name: $t('save'), action: fixItemFormSubmit, primary: true}
      ]">
      <div v-if="anomalyUnderFix.id" style="margin-bottom: 20px">
        <div><b>{{$t(`pos_mapping_anomalies.types.${anomalyUnderFix.type}`)}}</b></div>
        <div>{{$to(anomalyUnderFix.itemName)}} ({{$t(`item_type.${anomalyUnderFix.itemType}`)}})</div>
        <div style="white-space: pre; margin-top: 10px" v-html="anomalyUnderFix.description"></div>
      </div>
      <ItemPowerForm
        v-if="inventory && anomalyUnderFix.item"
        v-model="anomalyUnderFix.item"
        :show-tips="false"
        :inventory-id="inventory"
      />
    </Modal>

    <div style="display: flex; align-items: center; flex-direction: column">
      <button class="btn btn-primary" style="margin-bottom: 10px" @click="recomputeAnomalies" :disabled="recomputing">{{ $t('pos_mapping_anomalies.recompute_anomalies') }}</button>
      <div class="status-info" v-if="posInventoryUpdatedAt">{{ $t('pos_mapping_anomalies.pos_inventory_updated_at') }} {{posInventoryUpdatedAt}}</div>
      <div class="status-info" v-if="anomaliesUpdatedAt">{{ $t('pos_mapping_anomalies.anomalies_updated_at') }} {{anomaliesUpdatedAt}}</div>
    </div>

    <div class="table-global">
      <div class="muted-filter">
        <label>{{$t('pos_mapping_anomalies.hidden_filter_label')}}</label>
        <select v-model="localFilters.muted">
          <option :value="undefined">{{$t('all_f')}}</option>
          <option :value="true">{{$t('pos_mapping_anomalies.active_only')}}</option>
          <option :value="false">{{$t('pos_mapping_anomalies.hidden_only')}}</option>
        </select>
      </div>
      <div>
        <div v-if="filteredRows.length === 0"></div>
        <button class="btn btn-primary" v-else-if="anyRowIsNotMuted" @click="muteAllSelected">{{ $t('pos_mapping_anomalies.hide_all') }}</button>
        <button class="btn btn-primary" v-else @click="unmuteAllSelected">{{ $t('pos_mapping_anomalies.unhide_all') }}</button>
      </div>
    </div>

    <table class="anomaly-table">
      <tr>
        <th style="width: 120px">{{$t('pos_mapping_anomalies.reported_at')}}</th>
        <th style="width: 75px">{{ $t('type') }}</th>
        <th style="width: 200px">{{ $t('name') }}</th>
        <th style="width: 50px">{{ $t('external_id') }}</th>
        <th style="width: 100px">{{ $t('pos_mapping_anomalies.anomaly') }}</th>
        <th>{{ $t('description') }}</th>
        <th style="width: 42px"></th>
        <th style="width: 60px"></th>
        <th style="width: 20px"></th>
      </tr>

      <!-- Filters -->
      <tr class="filters">
        <td><input type="text" v-model="localFilters.reportedAt"></td>
        <td>
          <select v-model="localFilters['item.type']">
            <option :value="undefined"></option>
            <option v-for="iType of itemTypes" :value="iType">{{$t(`item_type.${iType}`)}}</option>
          </select>
        </td>
        <td><input type="text" v-model="localFilters['item.fullName']"></td>
        <td><input type="text" v-model="localFilters['item.externalId']"></td>
        <td>
          <select v-model="localFilters.type">
            <option :value="undefined"></option>
            <option v-for="aType of anomalyTypes" :value="aType">{{$t(`pos_mapping_anomalies.types.${aType}`)}}</option>
          </select>
        </td>
        <td><input type="text" v-model="localFilters.description"></td>
      </tr>

      <!-- data -->
      <tr v-for="a of filteredRows" :key="`anomaly_${a.id}`" :class="{'muted-row': a.muted}">
        <td>{{a.reportedAt}}</td>
        <td>{{$t(`item_type.${a.item.type}`)}}</td>
        <td>{{$to(a.item.fullName)}}</td>
        <td>{{a.item.externalId}}</td>
        <td>{{$t(`pos_mapping_anomalies.types.${a.type}`)}}</td>
        <td style="white-space: pre;" v-html="a.description"></td>
        <td>
          <button class="btn btn-primary" @click="() => fixItemForm(a)">Fix</button>
        </td>
        <td>
          <button v-if="a.muted" class="btn btn-primary" @click="() => unmute(a)" :disabled="a.muting">{{ $t('pos_mapping_anomalies.unhide') }}</button>
          <button v-else         class="btn btn-primary" @click="() => mute(a)" :disabled="a.muting">{{ $t('pos_mapping_anomalies.hide') }}</button>
        </td>
        <td>
          <div @click="() => openInTab(a.item)">
            <svgicon src="icon/link.svg" :height="13" :width="13"></svgicon>
          </div>
        </td>
      </tr>

    </table>

  </div>
</template>

<script>
import Vue from "vue";
import ItemPowerForm from "@/components/inventory/ItemPowerForm";
import {Core} from "@/core/Core";
import {convertUTCToLocal, debouncedComputedProperty, filterRows} from "@/vueUtils";

export default {
  name: "ExternalInventoryMappingAnomalies",
  components: {ItemPowerForm},
  props: {
    inventory: Number,
    company: Number, /* when accessing component from superAdmin */
    branch: Number, /* when accessing component from when superAdmin */
  },

  data() {
    return {
      recomputing: false,
      anomalies: [],
      posInventoryUpdatedAt: null,
      anomaliesUpdatedAt: null,
      anomalyUnderFix: {
        id: null,
        item: null,
      },
      itemFixData: null,
      localFilters: {},
      anomalyTypes: [
        'unmapped_not_found',
        'unmapped_undefined',
        'name_diff',
        'price_diff'
      ],
      itemTypes: [
        'item',
        'variant',
        'modifier',
        'combo',
        'supplement',
      ]
    }
  },

  computed: {
    localFiltersDebounced: debouncedComputedProperty(555, {}),
    filteredRows() {
      return filterRows(this.anomalies, this.localFiltersDebounced);
    },
    anyRowIsNotMuted() {
      return this.filteredRows.some(a => !a.muted);
    }
  },

  watch: {
    inventory: {
      immediate: true,
      async handler(i) {
        await this.load(i);
      }
    },
    localFilters: {
      immediate: true,
      deep: true,
      handler(val) {
        this.localFiltersDebounced = JSON.parse(JSON.stringify(val))
      }
    },
  },

  methods: {
    async load(inventory, forceRefresh = false) {
      this.anomalies = [];
      let query = forceRefresh ? {forceRefresh} : {}
      let response = await Core.noThrowRequest('GET', `/api/inventory/${inventory}/pos_mapping_anomalies`, query);
      if (response) {
        if (response.statuses) {
          this.anomaliesUpdatedAt = convertUTCToLocal(response.statuses.anomaliesUpdatedAt);
          this.posInventoryUpdatedAt = convertUTCToLocal(response.statuses.posInventoryUpdatedAt);
        }

        /* for backward compatibility, remove the condition eventually (response.anomalies is the correct structure) */
        let anomalies = response.anomalies ? response.anomalies : response;
        for (let a of anomalies) {
          this.remapAnomaly(a);
        }
        anomalies.sort(
          (a1, a2) => a1.reportedAt && a2.reportedAt
            ? a2.reportedAt.localeCompare(a1.reportedAt)
            : a1.id < a2.id ? 1 : -1
        );
        this.anomalies = anomalies;
      }
    },

    remapAnomaly(a) {
      if (a.type === 'unmapped') {
        a.type = a.data && a.data.externalId ? 'unmapped_not_found' : 'unmapped_undefined';
      }
      if (!a.reportedAt) {
        a.reportedAt = a.createdAt;
      }
      a.reportedAt = convertUTCToLocal(a.reportedAt);
      a.item.updatedAt = convertUTCToLocal(a.item.updatedAt);
      a.description = this.formattedErrorWithNewLines(a);
    },

    async recomputeAnomalies() {
      this.recomputing = true;
      await this.load(this.inventory, true)
      this.recomputing = false;
    },

    async unmute(anomaly) {
      await this.mute(anomaly, false);
    },

    async unmuteAllSelected() {
      await this.muteAllSelected(false);
    },

    async muteAllSelected(muting = true) {
      showSpinner();
      await Promise.allSettled( this.filteredRows.map(a => this.mute(a, muting)) );
      hideSpinner();
    },

    async mute(anomaly, muting = true) {
      if (muting === anomaly.muted) {
        return;
      }
      let action = muting ? 'mute' : 'unmute';
      let url = `/api/inventory/${this.inventory}/pos_mapping_anomalies/${anomaly.id}/${action}`;
      Vue.set(anomaly, 'muting', true);
      let response = await Core.noThrowRequest('PUT', url);
      Vue.set(anomaly, 'muting', false);
      if (response) {
        anomaly.muted = response.muted;
        anomaly.unhiddenBySystem = response.unhiddenBySystem;
        this.remapAnomaly(anomaly);
      }
    },

    fixItemForm(a) {
      let copy = JSON.parse(JSON.stringify(a));
      copy.itemName = JSON.parse(JSON.stringify(a.item.name));
      copy.itemType = a.item.type;

      let type = copy.type;
      let itemFields = ['id'];
      if (type === 'name_diff') {
        itemFields.push('name');
      } else if (type === 'price_diff') {
        itemFields.push('priceSchemes');
      } else if (type === 'unmapped_not_found' || type === 'unmapped_undefined') {
        itemFields.push('externalId', 'name', 'priceSchemes');
      } else {
        console.log(`unknown anomaly type '${type}'`)
        return;
      }
      copy.item = Object.entries(copy.item).reduce((acc, [k,v]) => {
        if (itemFields.includes(k)) {
          acc[k] = v;
        }
        return acc;
      }, {});

      this.anomalyUnderFix = copy;
      this.$modal.show('fix-item');
    },

    async fixItemFormSubmit() {
      let inventoryID = this.inventory;
      let anomalyID = this.anomalyUnderFix.id;
      let itemData = this.anomalyUnderFix.item;
      let url = `/api/inventory/${inventoryID}/pos_mapping_anomalies/${anomalyID}/fix`;
      this.$modal.hide('fix-item');

      showSpinner();
      let newAnomaly = await Core.noThrowRequest('PUT', url, itemData);
      hideSpinner();

      if (newAnomaly) {
        this.remapAnomaly(newAnomaly);
        let index = this.anomalies.findIndex(a => a.id === newAnomaly.id);
        if (index !== -1) {
          this.anomalies.splice(index, 1, newAnomaly);
        }

        /* update modified item if found in other anomalies */
        let item = newAnomaly.item;
        for (let a of this.anomalies) {
          if (a.id !== newAnomaly.id && a.item.id === item.id) {
            a.item = JSON.parse(JSON.stringify(item));
            this.remapAnomaly(a);
          }
        }
      }
    },

    openInTab(item) {
      /* careful with the query parameters, this component is accessible from superAdmin */
      let query = {
        inventory: this.inventory,
        company: this.$route.query.company || this.company,
        branch: this.$route.query.branch || this.branch
      }
      let route = null;
      if (item.type === 'modifier') {
        query.id = item.id;
        route = '/modifiers/edit';
      } else if (item.type === 'combo') {
        query.id = item.id;
        route = '/choices/edit'
      } else if (item.type === 'supplement') {
        query.supplement = item.id;
        route = '/supplement/edit'
      } else {
        query.item = item.parentId || item.id;
        route = '/item/edit';
      }

      route = route + '?' + Object.entries(query).map(([k,v]) => `${k}=${v}`).join('&');
      let resolvedRoute = this.$router.resolve(route);
      window.open(resolvedRoute.href, "_blank");
    },

    formattedErrorWithNewLines(anomaly) {
      return this.formattedErrorAsList(anomaly).join('\n');
    },
    formattedErrorAsList(anomaly) {
      let lines = [];
      if (anomaly.unhiddenBySystem) {
        lines.push("<small style='color: indianred'>" + this.$t('pos_mapping_anomalies.unhidden_by_system') + "</small>");
      }
      if (this.anomaliesUpdatedAt && anomaly.item.updatedAt && this.anomaliesUpdatedAt < anomaly.item.updatedAt) {
        lines.push("<small>" + this.$t('pos_mapping_anomalies.item_modified_since') + "</small>");
      }

      let type = anomaly.type;
      if (type === 'unmapped_not_found') {
        let externalID = anomaly.data && anomaly.data.externalId ? anomaly.data.externalId : null;
        lines.push(externalID);
      }
      else if (type === 'name_diff') {
        let posName = anomaly.data.name.pos;
        let localName = anomaly.data.name.local;
        lines.push(this.$t('pos_mapping_anomalies.pos_value').replace('%V%', posName));
        lines.push(this.$t('pos_mapping_anomalies.local_value').replace('%V%', localName));
      }
      else if (type === 'price_diff') {
        for (let [method, d] of Object.entries(anomaly.data)) {
          let posPrice = this.$tc(d.pos);
          let localPrice = this.$tc(d.local);
          let methodTranslated = this.$t(`deliveryMethod.${method}`)
          lines.push(`${methodTranslated}: ${this.$t('pos_mapping_anomalies.pos_value').replace('%V%', posPrice)}`);
          lines.push(`${methodTranslated}: ${this.$t('pos_mapping_anomalies.local_value').replace('%V%', localPrice)}`);
        }
      }
      return lines;
    }
  }
}
</script>

<style lang="scss" scoped>
.status-info {
  font-size: smaller;
}

.table-global {
  display: flex;
  justify-content: space-between;

  .muted-filter {
    display: flex;
    margin: 10px 0 4px;

    select {
      padding: 0;
      height: 24px;
      margin-left: 10px;
    }
  }

  button:not(:last-child) {
    margin-right: 5px;
  }
}

.anomaly-table {
  width: 100%;

  td,
  th {
    padding: 2px 2px;
    text-align: center;
  }

  button {
    padding: 3px 8px;
  }

  tr:nth-child(odd) {
    background-color: #eee;
  }
  tr+.muted-row {
    background-color: rgba(144, 12, 63, 0.25);
  }

  th {
    white-space: nowrap;
    background-color: #ddd;
  }

  .filters {
    input, select {
      padding: 0;
      height: 24px;
      width: 100%;
    }
  }
}
</style>